import React, { useCallback, useMemo, useRef } from 'react';
import './MfaOtpCodeSlots.css';

export const MFA_OTP_SLOT_COUNT = 6;

/** Six single-digit OTP spaces; keeps `value` as one concatenated string for verify APIs. */
export default function MfaOtpCodeSlots({
  id = 'mfa-otp-code',
  value = '',
  onChange,
  disabled = false,
  'aria-label': ariaLabel = 'Verification code',
  className = '',
}) {
  const inputRefs = useRef([]);
  const digits = useMemo(() => {
    const cleaned = String(value || '').replace(/\D/g, '').slice(0, MFA_OTP_SLOT_COUNT);
    return Array.from({ length: MFA_OTP_SLOT_COUNT }, (_, i) => cleaned[i] || '');
  }, [value]);

  const commitDigits = useCallback(
    (nextDigits) => {
      if (typeof onChange !== 'function') return;
      onChange(nextDigits.join('').slice(0, MFA_OTP_SLOT_COUNT));
    },
    [onChange]
  );

  const focusSlot = useCallback((index) => {
    const el = inputRefs.current[index];
    if (el && typeof el.focus === 'function') {
      el.focus();
      el.select?.();
    }
  }, []);

  const handleSlotChange = useCallback(
    (index, raw) => {
      const onlyDigits = String(raw || '').replace(/\D/g, '');
      if (!onlyDigits) {
        const next = [...digits];
        next[index] = '';
        commitDigits(next);
        return;
      }
      if (onlyDigits.length > 1) {
        const next = [...digits];
        const chars = onlyDigits.slice(0, MFA_OTP_SLOT_COUNT - index).split('');
        chars.forEach((ch, offset) => {
          next[index + offset] = ch;
        });
        commitDigits(next);
        focusSlot(Math.min(index + chars.length, MFA_OTP_SLOT_COUNT - 1));
        return;
      }
      const next = [...digits];
      next[index] = onlyDigits[0];
      commitDigits(next);
      if (index < MFA_OTP_SLOT_COUNT - 1) focusSlot(index + 1);
    },
    [digits, commitDigits, focusSlot]
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      if (event.key === 'Backspace') {
        if (digits[index]) {
          const next = [...digits];
          next[index] = '';
          commitDigits(next);
          event.preventDefault();
          return;
        }
        if (index > 0) {
          const next = [...digits];
          next[index - 1] = '';
          commitDigits(next);
          focusSlot(index - 1);
          event.preventDefault();
        }
        return;
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        focusSlot(index - 1);
      } else if (event.key === 'ArrowRight' && index < MFA_OTP_SLOT_COUNT - 1) {
        event.preventDefault();
        focusSlot(index + 1);
      }
    },
    [digits, commitDigits, focusSlot]
  );

  const handlePaste = useCallback(
    (event) => {
      const pasted = event.clipboardData?.getData('text') || '';
      const onlyDigits = pasted.replace(/\D/g, '').slice(0, MFA_OTP_SLOT_COUNT);
      if (!onlyDigits) return;
      event.preventDefault();
      const next = Array.from({ length: MFA_OTP_SLOT_COUNT }, (_, i) => onlyDigits[i] || '');
      commitDigits(next);
      focusSlot(Math.min(onlyDigits.length, MFA_OTP_SLOT_COUNT - 1));
    },
    [commitDigits, focusSlot]
  );

  return (
    <div
      className={`mfa-otp-slots profile-settings-mfa-otp-slots${className ? ` ${className}` : ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      {digits.map((digit, index) => (
        <input
          key={`${id}-${index}`}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          id={index === 0 ? id : `${id}-${index + 1}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`${ariaLabel} digit ${index + 1}`}
          className="mfa-otp-slot profile-settings-input profile-settings-mfa-otp-slot"
          onChange={(e) => handleSlotChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
